/* Hallmark · macrostructure: Map / Diagram · theme: Tarbiyah Heritage · audience: returning and active participants · use: distinguish current six-tier results from read-only legacy results · pre-emit critique: P5 H5 E5 S5 R5 V5 · contrast: pass (40–41) · responsive: pass (34, 49) */

import { useState, useMemo } from 'react';
import { Link } from 'react-router';
import {
  Printer,
  Copy,
  Check,
  Award,
  BookOpen,
  GraduationCap,
  ArrowLeft,
  ChevronRight,
  Loader2,
  History,
  Search,
  X,
} from 'lucide-react';
import { useEnrollments } from '../hooks/useEnrollments';
import { useProgramTranscript, useLegacyTranscripts } from '../hooks/useTranscripts';
import { useLanguage } from '../lib/LanguageContext';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';
import { useAuthStore } from '../store/auth';
import { useParticipantNip } from '../hooks/useParticipantNip';
import type { LegacyTranscriptDetail } from '@lms/shared/types';

type TranscriptView = 'CURRENT' | 'LEGACY';
type TranscriptTab = 'LMS' | 'LEGACY';

interface TranscriptsProps {
  view?: TranscriptView;
}

export function Transcripts({ view = 'CURRENT' }: TranscriptsProps) {
  const { user } = useAuthStore();
  const participantNip = useParticipantNip();
  const { t } = useLanguage();

  const { data: enrollments, isLoading: loadingEnrollments } = useEnrollments(
    {
      userId: user?.id,
    },
    view === 'CURRENT'
  );
  const { data: legacyData, isLoading: loadingLegacy } = useLegacyTranscripts(
    user?.id,
    view === 'LEGACY'
  );

  const [selectedProgramId, setSelectedProgramId] = useState<string>('');
  const [selectedLegacySemester, setSelectedLegacySemester] = useState<string>('ALL');
  const [selectedLegacyProgram, setSelectedLegacyProgram] = useState<string>('ALL');
  const [legacySearchQuery, setLegacySearchQuery] = useState('');
  const [copiedCode, setCopiedCode] = useState(false);
  const [detailModalSubject, setDetailModalSubject] = useState<LegacyTranscriptDetail | null>(null);

  const hasEnrollments = Array.isArray(enrollments) && enrollments.length > 0;
  const hasLegacyTranscripts = Boolean(
    legacyData && legacyData.transcripts && legacyData.transcripts.length > 0
  );

  const activeTab: TranscriptTab = view === 'LEGACY' ? 'LEGACY' : 'LMS';

  // Set default selected program once enrollments load
  const activeProgramId = useMemo(() => {
    if (selectedProgramId) return selectedProgramId;
    if (enrollments && enrollments.length > 0) return enrollments[0].programId;
    return '';
  }, [selectedProgramId, enrollments]);

  const { data: transcript, isLoading: loadingTranscript } = useProgramTranscript(
    activeTab === 'LMS' ? activeProgramId || undefined : undefined
  );

  const handleCopyCode = (code: string) => {
    void navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  // Filter legacy transcripts
  const filteredLegacyTranscripts = useMemo(() => {
    if (!legacyData?.transcripts) return [];
    return legacyData.transcripts.filter(item => {
      if (selectedLegacySemester !== 'ALL' && item.semester !== Number(selectedLegacySemester)) {
        return false;
      }
      if (
        selectedLegacyProgram !== 'ALL' &&
        item.programName !== selectedLegacyProgram &&
        item.program.name !== selectedLegacyProgram &&
        item.program.id !== selectedLegacyProgram &&
        item.program.code !== selectedLegacyProgram
      ) {
        return false;
      }
      if (legacySearchQuery.trim()) {
        const q = legacySearchQuery.toLowerCase();
        const matchesName =
          (item.subjectName && item.subjectName.toLowerCase().includes(q)) ||
          item.program.name.toLowerCase().includes(q);
        const matchesCode =
          (item.subjectCode && item.subjectCode.toLowerCase().includes(q)) ||
          (item.program.code && item.program.code.toLowerCase().includes(q)) ||
          false;
        const matchesProg = item.programName && item.programName.toLowerCase().includes(q);
        const matchesPred = item.predicate?.toLowerCase().includes(q) || false;
        if (!matchesName && !matchesCode && !matchesProg && !matchesPred) return false;
      }
      return true;
    });
  }, [legacyData, selectedLegacySemester, selectedLegacyProgram, legacySearchQuery]);

  const groupedLegacySemesters = useMemo(() => {
    const map = new Map<number, LegacyTranscriptDetail[]>();
    filteredLegacyTranscripts.forEach(item => {
      const sem = item.semester ?? 0;
      const arr = map.get(sem) || [];
      arr.push(item);
      map.set(sem, arr);
    });

    return Array.from(map.entries())
      .sort(([a], [b]) => a - b)
      .map(([semester, subjects]) => {
        const originalSemesterGroup = legacyData?.semesters.find(s => s.semester === semester);
        const semesterGpa = originalSemesterGroup?.semesterGpa ?? null;
        const totalCredits =
          originalSemesterGroup?.totalCredits ??
          subjects.reduce((sum, s) => sum + (s.credit ?? 1), 0);
        const passedCount = subjects.filter(
          s => s.isCourseFinished && s.predicate !== 'E' && s.predicate !== 'D'
        ).length;

        return {
          semester,
          semesterGpa,
          totalCredits,
          passedCount,
          programName: subjects[0]?.programName,
          subjects,
        };
      });
  }, [filteredLegacyTranscripts, legacyData]);

  const uniqueLegacyPrograms = useMemo(() => {
    if (!legacyData?.transcripts) return [];
    const set = new Set<string>();
    const progs: Array<{ id: string; name: string; code?: string | null }> = [];
    legacyData.transcripts.forEach(t => {
      const pName = t.programName || t.program.name;
      if (!set.has(pName)) {
        set.add(pName);
        progs.push({ id: pName, name: pName, code: t.programCode || t.program.code });
      }
    });
    return progs;
  }, [legacyData]);

  const isLoading =
    activeTab === 'LMS'
      ? loadingEnrollments || (activeProgramId ? loadingTranscript : false)
      : loadingLegacy;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[450px] space-y-4">
        <Loader2 className="w-9 h-9 text-primary animate-spin" />
        <p className="text-sm font-body text-on-surface-variant font-medium">
          <Bilingual {...copy.transcripts.loading} />
        </p>
      </div>
    );
  }

  const hasActiveViewData = activeTab === 'LMS' ? hasEnrollments : hasLegacyTranscripts;

  if (!hasActiveViewData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-20 text-center space-y-6 animate-fadeIn">
        <div className="bg-surface-container-low rounded-full w-24 h-24 flex items-center justify-center mx-auto shadow-level1">
          <GraduationCap size={44} className="text-primary/40" />
        </div>
        <h2 className="text-2xl font-headline font-bold text-on-surface">
          <Bilingual
            {...(activeTab === 'LMS'
              ? copy.transcripts.emptyTitle
              : copy.transcripts.legacyEmptyTitle)}
          />
        </h2>
        <p className="text-on-surface-variant max-w-md leading-relaxed text-sm">
          <Bilingual
            {...(activeTab === 'LMS'
              ? copy.transcripts.emptyDesc
              : copy.transcripts.legacyEmptyDesc)}
          />
        </p>
        <Link
          to={activeTab === 'LMS' ? '/programs' : '/transcripts'}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-on-primary font-label font-bold uppercase tracking-wider text-xs shadow-level1 transition-colors hover:bg-primary-container active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          <Bilingual
            {...(activeTab === 'LMS'
              ? copy.transcripts.browsePrograms
              : copy.transcripts.currentTranscriptNavigation)}
          />
          <ChevronRight size={16} />
        </Link>
      </div>
    );
  }

  const selectedEnrollment = enrollments?.find(e => e.programId === activeProgramId);

  return (
    <div className="space-y-8 animate-fadeIn pb-16">
      <section className="print:hidden flex flex-col gap-4 rounded-3xl bg-surface-container-low p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <div className="min-w-0 space-y-1.5">
          <div className="flex items-center gap-2 text-primary">
            {activeTab === 'LMS' ? <BookOpen size={18} /> : <History size={18} />}
            <span className="text-xs font-label font-bold uppercase tracking-[0.14em]">
              <Bilingual
                {...(activeTab === 'LMS'
                  ? copy.transcripts.currentTranscriptContext
                  : copy.transcripts.legacyArchiveContext)}
              />
            </span>
          </div>
          <p className="m-0 max-w-2xl text-sm leading-relaxed text-on-surface-variant">
            <Bilingual
              {...(activeTab === 'LMS'
                ? copy.transcripts.activeHierarchyNotice
                : copy.transcripts.legacyHierarchyNotice)}
            />
          </p>
        </div>
        <Link
          to={activeTab === 'LMS' ? '/legacy-transcripts' : '/transcripts'}
          className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 self-start whitespace-nowrap rounded-xl bg-surface-container-highest px-4 py-2.5 text-xs font-label font-bold text-primary no-underline transition-colors hover:bg-primary hover:text-on-primary active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:self-auto"
        >
          {activeTab === 'LMS' ? <History size={16} /> : <BookOpen size={16} />}
          <Bilingual
            {...(activeTab === 'LMS'
              ? copy.transcripts.legacyTranscriptNavigation
              : copy.transcripts.currentTranscriptNavigation)}
          />
        </Link>
      </section>

      {activeTab === 'LMS' && hasEnrollments && (
        <div className="space-y-8 animate-fadeIn">
          <div className="print:hidden flex flex-wrap items-end justify-between gap-4 rounded-2xl bg-surface-container-lowest p-4 shadow-level1">
            <div className="flex min-w-0 flex-col gap-1.5">
              <label
                htmlFor="active-transcript-program"
                className="text-xs font-label font-bold uppercase tracking-wider text-on-surface-variant"
              >
                <Bilingual {...copy.transcripts.programName} />
              </label>
              <select
                id="active-transcript-program"
                value={activeProgramId}
                onChange={e => setSelectedProgramId(e.target.value)}
                className="min-h-11 min-w-[220px] rounded-xl bg-surface-container-high px-4 py-2.5 text-xs font-body font-bold text-on-surface outline-2 outline-transparent transition-colors hover:bg-surface-container-highest focus-visible:outline-primary sm:text-sm"
              >
                {enrollments?.map(enr => (
                  <option key={enr.programId} value={enr.programId}>
                    {enr.program.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handlePrint}
              disabled={!transcript}
              className="flex min-h-11 items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-xs font-label font-bold uppercase tracking-wider text-on-primary shadow-level1 transition-colors hover:bg-primary-container active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-55"
            >
              <Printer size={15} />
              <Bilingual {...copy.transcripts.printTranscript} />
            </button>
          </div>

          {transcript && (
            <div className="bg-surface-container-lowest rounded-3xl p-6 sm:p-10 shadow-level2 border border-outline-variant/10 print:border-none print:shadow-none print:p-0 space-y-8">
              <div className="text-center space-y-3 pb-8 border-b border-outline-variant/15">
                <div className="flex justify-center items-center gap-4">
                  <img
                    src="/logo.png"
                    alt="TSL Logo"
                    className="h-14 sm:h-16 w-auto object-contain"
                  />
                </div>
                <div>
                  <h2 className="text-lg sm:text-2xl font-headline font-black text-on-surface tracking-wider uppercase">
                    TARBIYAH SUNNAH LEARNING
                  </h2>
                  <p className="text-xs sm:text-sm font-body text-on-surface-variant font-medium">
                    Pendidikan Jarak Jauh (PJJ) Dirasah Islamiyah & Bahasa Arab
                  </p>
                </div>
                <div className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-headline font-bold text-xs sm:text-sm uppercase tracking-widest">
                  <Bilingual {...copy.transcripts.officialTranscriptTitle} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 p-5 bg-surface-container-low/40 rounded-2xl border border-outline-variant/10 text-xs font-body">
                <div>
                  <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                    <Bilingual {...copy.transcripts.studentName} />
                  </span>
                  <span className="text-sm font-headline font-bold text-on-surface block mt-0.5">
                    {transcript.user.name}
                  </span>
                </div>

                <div>
                  <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                    <Bilingual {...copy.transcripts.studentId} />
                  </span>
                  <span className="text-sm font-mono font-bold text-primary block mt-0.5">
                    {participantNip ||
                      transcript.user.userCode ||
                      transcript.user.id.slice(0, 8).toUpperCase()}
                  </span>
                </div>

                <div>
                  <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                    <Bilingual {...copy.transcripts.programName} />
                  </span>
                  <span className="text-sm font-headline font-bold text-on-surface block mt-0.5 line-clamp-1">
                    {transcript.program.name}
                  </span>
                </div>

                <div>
                  <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                    <Bilingual {...copy.transcripts.graduationStatus} />
                  </span>
                  <span
                    className={`inline-block mt-1 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                      transcript.summary.isGraduated
                        ? 'bg-success/15 text-success border border-success/30'
                        : 'bg-amber-500/15 text-amber-600 border border-amber-500/30'
                    }`}
                  >
                    {transcript.summary.isGraduated ? (
                      <Bilingual {...copy.transcripts.passed} />
                    ) : (
                      <Bilingual {...copy.transcripts.inProgressStatus} />
                    )}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-headline font-bold text-on-surface flex items-center gap-2">
                    <BookOpen size={16} className="text-primary" />
                    <span>Daftar Nilai Capaian Mata Pelajaran (Kurikulum 6 Tahap)</span>
                  </h3>
                </div>

                <div className="overflow-x-auto rounded-2xl border border-outline-variant/15">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-surface-container-low/60 border-b border-outline-variant/10 text-[10px] sm:text-xs font-label font-black uppercase tracking-wider text-on-surface-variant">
                        <th className="p-3 sm:p-4 text-center w-12">No</th>
                        <th className="p-3 sm:p-4">
                          <Bilingual {...copy.transcripts.subjectName} />
                        </th>
                        <th className="p-3 sm:p-4 text-center">Tahapan</th>
                        <th className="p-3 sm:p-4 text-center">Kehadiran</th>
                        <th className="p-3 sm:p-4 text-center">
                          <Bilingual {...copy.transcripts.finalScore} />
                        </th>
                        <th className="p-3 sm:p-4 text-center">
                          <Bilingual {...copy.transcripts.gradeLetter} />
                        </th>
                        <th className="p-3 sm:p-4 text-center">
                          <Bilingual {...copy.transcripts.academicPredicate} />
                        </th>
                        <th className="p-3 sm:p-4 text-center">
                          <Bilingual {...copy.transcripts.subjectStatus} />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/10 text-xs font-body">
                      {transcript.subjects.map((subj, idx) => (
                        <tr
                          key={subj.id}
                          className="hover:bg-surface-container-low/40 transition-colors"
                        >
                          <td className="p-3 sm:p-4 text-center font-mono text-on-surface-variant">
                            {idx + 1}
                          </td>
                          <td className="p-3 sm:p-4">
                            <span className="font-headline font-bold text-on-surface block">
                              {subj.name}
                            </span>
                            {subj.code && (
                              <span className="text-[10px] font-mono text-primary font-bold">
                                {subj.code}
                              </span>
                            )}
                          </td>
                          <td className="p-3 sm:p-4 text-center text-on-surface-variant font-medium">
                            {subj.stageCode ? `Tahap ${subj.stageCode}` : subj.stageName}
                          </td>
                          <td className="p-3 sm:p-4 text-center font-mono font-medium">
                            {subj.attendancePercentage}%
                          </td>
                          <td className="p-3 sm:p-4 text-center font-mono font-bold text-on-surface">
                            {subj.evaluationScore != null ? `${subj.evaluationScore}%` : '-'}
                            {subj.evaluationScore == null && subj.priorLearningRecognition && (
                              <span className="mt-1 block text-sm font-medium text-primary">
                                {subj.priorLearningRecognition.score}%
                                <span className="mt-1 block font-body text-xs leading-relaxed">
                                  {t(copy.programDetail.priorLearningGradeSource)}
                                  {subj.priorLearningRecognition.scoreIsEstimated && (
                                    <span className="mt-1 block">
                                      {t(copy.programDetail.priorLearningEstimated)}
                                    </span>
                                  )}
                                </span>
                              </span>
                            )}
                          </td>
                          <td className="p-3 sm:p-4 text-center font-mono font-bold text-primary text-sm">
                            {subj.letterGrade}
                          </td>
                          <td className="p-3 sm:p-4 text-center">
                            <span className="text-xs font-arabic font-bold block text-primary">
                              {subj.predicate.arabic}
                            </span>
                            <span className="text-[10px] text-on-surface-variant font-medium">
                              {subj.predicate.indonesian}
                            </span>
                          </td>
                          <td className="p-3 sm:p-4 text-center">
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-[9px] font-headline font-bold tracking-wider uppercase ${
                                subj.isPassed
                                  ? 'bg-success/15 text-success'
                                  : 'bg-amber-500/15 text-amber-600'
                              }`}
                            >
                              {subj.isPassed ? 'TUNTAS' : 'BELUM'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-5 rounded-2xl bg-surface-container-low/60 border border-outline-variant/10 text-xs font-body">
                <div>
                  <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                    <Bilingual {...copy.transcripts.gradeAverage} />
                  </span>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-2xl font-headline font-black text-primary">
                      {transcript.summary.cumulativeScore}%
                    </span>
                    <span className="text-sm font-bold text-on-surface">
                      ({transcript.summary.letterGrade})
                    </span>
                  </div>
                </div>

                <div>
                  <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                    <Bilingual {...copy.transcripts.yudisium} />
                  </span>
                  <div className="mt-1">
                    <span className="text-sm font-headline font-bold text-on-surface block">
                      {transcript.summary.predicate.indonesian}
                    </span>
                    <span className="text-xs font-arabic font-bold text-secondary block">
                      {transcript.summary.predicate.arabic}
                    </span>
                  </div>
                </div>

                <div>
                  <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                    Mata Pelajaran Tuntas
                  </span>
                  <div className="text-2xl font-headline font-black text-success mt-1">
                    {transcript.summary.passedSubjects} / {transcript.summary.totalSubjects}
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-outline-variant/15 flex flex-col sm:flex-row justify-between items-center gap-6">
                <div className="space-y-2 text-center sm:text-left">
                  <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant block">
                    <Bilingual {...copy.transcripts.verificationCode} />
                  </span>
                  <div className="flex items-center gap-2 p-2 rounded-xl bg-surface-container-low border border-outline-variant/15 font-mono text-xs font-bold text-primary">
                    <span>{transcript.summary.verificationCode}</span>
                    <button
                      type="button"
                      onClick={() => handleCopyCode(transcript.summary.verificationCode)}
                      className="p-1 rounded hover:bg-surface-container text-on-surface-variant hover:text-primary transition-colors"
                      title="Salin Kode Verifikasi"
                    >
                      {copiedCode ? (
                        <Check size={14} className="text-success" />
                      ) : (
                        <Copy size={14} />
                      )}
                    </button>
                  </div>
                </div>

                <div className="text-center space-y-2 sm:self-end">
                  <span className="text-[10px] text-on-surface-variant font-medium block">
                    Bandung,{' '}
                    {new Date(transcript.summary.issuedAt).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                  <div className="h-14 flex items-center justify-center">
                    <div className="w-28 h-10 border border-dashed border-primary/30 rounded-lg flex items-center justify-center text-[10px] font-bold text-primary/40 uppercase tracking-widest">
                      Tanda Tangan
                    </div>
                  </div>
                  <span className="text-xs font-headline font-bold text-on-surface block">
                    <Bilingual {...copy.transcripts.directorSignature} />
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'LEGACY' && (
        <div className="space-y-8 animate-fadeIn">
          <div className="print:hidden relative overflow-hidden rounded-3xl bg-surface-container-lowest p-6 shadow-level1 sm:p-8">
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
              <div className="min-w-0 max-w-2xl space-y-3">
                <div className="inline-flex items-center gap-2 rounded-full bg-secondary/15 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-secondary">
                  <History size={14} />
                  <Bilingual {...copy.transcripts.legacyArchiveContext} />
                </div>
                <h1 className="min-w-0 overflow-wrap-anywhere text-3xl font-headline font-black tracking-tight text-on-surface sm:text-4xl">
                  <Bilingual {...copy.transcripts.legacyTitle} />
                </h1>
                <p className="max-w-[65ch] text-sm leading-relaxed text-on-surface-variant sm:text-base">
                  <Bilingual {...copy.transcripts.legacySubtitle} />
                </p>
              </div>

              <button
                type="button"
                onClick={handlePrint}
                className="inline-flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-xl bg-primary px-5 py-3 text-xs font-label font-bold uppercase tracking-wider text-on-primary shadow-level1 transition-colors hover:bg-primary-container active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                <Printer size={16} />
                <Bilingual {...copy.transcripts.printLegacyTranscript} />
              </button>
            </div>
          </div>

          {legacyData?.programs && legacyData.programs.length > 0 && (
            <section className="print:hidden rounded-3xl bg-surface-container-low p-5 sm:p-6">
              <div className="max-w-[65ch] space-y-1.5">
                <h2 className="text-xl font-headline font-black tracking-tight text-on-surface">
                  <Bilingual {...copy.transcripts.legacyArchiveMap} />
                </h2>
                <p className="m-0 text-sm leading-relaxed text-on-surface-variant">
                  <Bilingual {...copy.transcripts.legacyArchiveMapHelp} />
                </p>
              </div>
              <div className="mt-5 flex gap-3 overflow-x-auto pb-1">
                <button
                  type="button"
                  onClick={() => setSelectedLegacyProgram('ALL')}
                  aria-pressed={selectedLegacyProgram === 'ALL'}
                  className={`min-h-11 shrink-0 whitespace-nowrap rounded-xl px-4 py-2.5 text-left text-xs font-label font-bold transition-colors active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
                    selectedLegacyProgram === 'ALL'
                      ? 'bg-primary text-on-primary shadow-level1'
                      : 'bg-surface-container-lowest text-on-surface-variant hover:bg-surface-container-highest hover:text-primary'
                  }`}
                >
                  <Bilingual {...copy.transcripts.filterAllPrograms} />
                </button>
                {legacyData.programs.map(program => {
                  const isSelected = selectedLegacyProgram === program.programName;
                  return (
                    <button
                      key={`${program.programCode ?? 'legacy'}-${program.programName}`}
                      type="button"
                      onClick={() => setSelectedLegacyProgram(program.programName)}
                      aria-pressed={isSelected}
                      className={`min-h-11 shrink-0 whitespace-nowrap rounded-xl px-4 py-2.5 text-left text-xs font-label font-bold transition-colors active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
                        isSelected
                          ? 'bg-primary text-on-primary shadow-level1'
                          : 'bg-surface-container-lowest text-on-surface-variant hover:bg-surface-container-highest hover:text-primary'
                      }`}
                    >
                      <span className="block">{program.programName}</span>
                      <span className="mt-0.5 block font-mono text-[10px] opacity-70">
                        {program.semesters.length} {t(copy.transcripts.filterSemesterPrefix)} ·{' '}
                        {program.totalSubjects}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {legacyData?.summary && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,.85fr)_minmax(0,.85fr)_minmax(0,1fr)]">
              <div className="space-y-2 rounded-3xl bg-surface-container-lowest p-6 shadow-level1">
                <div className="flex justify-between items-start">
                  <span className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant opacity-70">
                    <Bilingual {...copy.transcripts.cumulativeGpa} />
                  </span>
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-headline font-bold bg-primary/10 text-primary">
                    {legacyData.summary.overallPredicate.letter}
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-headline font-black text-primary m-0">
                    {legacyData.summary.cumulativeGpa != null
                      ? legacyData.summary.cumulativeGpa.toFixed(2)
                      : '-'}
                  </p>
                  <span className="text-xs text-on-surface-variant font-mono">/ 4.00</span>
                </div>
                <p className="text-xs font-arabic font-bold text-secondary m-0">
                  {legacyData.summary.overallPredicate.arabic} •{' '}
                  {legacyData.summary.overallPredicate.indonesian}
                </p>
              </div>

              <div className="space-y-2 rounded-3xl bg-surface-container-lowest p-6 shadow-level1">
                <span className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant opacity-70">
                  <Bilingual {...copy.transcripts.totalSemestersCompleted} />
                </span>
                <p className="text-3xl font-headline font-black text-on-surface m-0">
                  {legacyData.summary.totalSemesters}
                </p>
                <p className="text-xs font-body text-on-surface-variant opacity-70 m-0">
                  Telah direkapitulasi per semester
                </p>
              </div>

              <div className="space-y-2 rounded-3xl bg-surface-container-lowest p-6 shadow-level1">
                <span className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant opacity-70">
                  <Bilingual {...copy.transcripts.totalCreditsLoad} />
                </span>
                <p className="text-3xl font-headline font-black text-secondary m-0">
                  {legacyData.summary.totalCredits}{' '}
                  <span className="text-sm font-label text-on-surface-variant">SKS</span>
                </p>
                <p className="text-xs font-body text-on-surface-variant opacity-70 m-0">
                  Total beban studi masa lalu
                </p>
              </div>

              <div className="space-y-2 rounded-3xl bg-surface-container-lowest p-6 shadow-level1">
                <span className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant opacity-70">
                  <Bilingual {...copy.transcripts.totalSubjectsPassed} />
                </span>
                <p className="text-3xl font-headline font-black text-emerald-600 m-0">
                  {legacyData.summary.passedSubjects}{' '}
                  <span className="text-sm font-label text-on-surface-variant">
                    / {legacyData.summary.totalSubjects}
                  </span>
                </p>
                <p className="text-xs font-body text-emerald-700 font-bold m-0">
                  {legacyData.summary.totalSubjects > 0
                    ? `${Math.round((legacyData.summary.passedSubjects / legacyData.summary.totalSubjects) * 100)}% Kelulusan`
                    : '-'}
                </p>
              </div>
            </div>
          )}

          <div className="print:hidden rounded-2xl bg-surface-container-lowest p-4 shadow-level1 sm:p-5">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.35fr)_auto] lg:items-end">
              <div className="min-w-0 space-y-1.5">
                <label
                  htmlFor="legacy-semester"
                  className="block text-xs font-label font-bold uppercase tracking-wider text-on-surface-variant"
                >
                  <Bilingual {...copy.transcripts.filterSemesterPrefix} />
                </label>
                <select
                  id="legacy-semester"
                  value={selectedLegacySemester}
                  onChange={e => setSelectedLegacySemester(e.target.value)}
                  className="min-h-11 w-full rounded-xl bg-surface-container-high px-4 py-2.5 text-xs font-body font-bold text-on-surface outline-2 outline-transparent transition-colors hover:bg-surface-container-highest focus-visible:outline-primary"
                >
                  <option value="ALL">{t(copy.transcripts.filterAllSemesters)}</option>
                  {legacyData?.semesters.map(s => (
                    <option key={s.semester} value={String(s.semester)}>
                      {t(copy.transcripts.filterSemesterPrefix)} {s.semester}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-0 space-y-1.5">
                <label
                  htmlFor="legacy-program"
                  className="block text-xs font-label font-bold uppercase tracking-wider text-on-surface-variant"
                >
                  <Bilingual {...copy.transcripts.programParentName} />
                </label>
                <select
                  id="legacy-program"
                  value={selectedLegacyProgram}
                  onChange={e => setSelectedLegacyProgram(e.target.value)}
                  className="min-h-11 w-full rounded-xl bg-surface-container-high px-4 py-2.5 text-xs font-body font-bold text-on-surface outline-2 outline-transparent transition-colors hover:bg-surface-container-highest focus-visible:outline-primary"
                >
                  <option value="ALL">{t(copy.transcripts.filterAllPrograms)}</option>
                  {uniqueLegacyPrograms.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="min-w-0 space-y-1.5">
                <label
                  htmlFor="legacy-search"
                  className="block text-xs font-label font-bold uppercase tracking-wider text-on-surface-variant"
                >
                  <Bilingual {...copy.transcripts.courseName} />
                </label>
                <div className="relative">
                  <Search
                    size={16}
                    className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-50"
                  />
                  <input
                    id="legacy-search"
                    type="search"
                    placeholder={t(copy.transcripts.searchSubjectPlaceholder)}
                    value={legacySearchQuery}
                    onChange={e => setLegacySearchQuery(e.target.value)}
                    className="min-h-11 w-full rounded-xl bg-surface-container-high py-2.5 pl-10 pr-10 text-xs font-body text-on-surface outline-2 outline-transparent transition-colors hover:bg-surface-container-highest focus-visible:outline-primary"
                  />
                  {legacySearchQuery && (
                    <button
                      type="button"
                      onClick={() => setLegacySearchQuery('')}
                      aria-label={t(copy.transcripts.clearLegacyFilters)}
                      className="absolute right-1.5 top-1/2 grid min-h-9 min-w-9 -translate-y-1/2 place-items-center rounded-lg text-on-surface-variant transition-colors hover:bg-surface-container-highest hover:text-on-surface active:translate-y-[calc(-50%+1px)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setSelectedLegacySemester('ALL');
                  setSelectedLegacyProgram('ALL');
                  setLegacySearchQuery('');
                }}
                disabled={
                  selectedLegacySemester === 'ALL' &&
                  selectedLegacyProgram === 'ALL' &&
                  legacySearchQuery.length === 0
                }
                className="min-h-11 whitespace-nowrap rounded-xl bg-surface-container-high px-4 py-2.5 text-xs font-label font-bold text-primary transition-colors hover:bg-surface-container-highest active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-55"
              >
                <Bilingual {...copy.transcripts.clearLegacyFilters} />
              </button>
            </div>
            <p aria-live="polite" className="mb-0 mt-3 text-xs font-body text-on-surface-variant">
              {filteredLegacyTranscripts.length}{' '}
              <Bilingual {...copy.transcripts.legacyFilterResults} />
            </p>
          </div>

          <div className="bg-surface-container-lowest rounded-3xl p-6 sm:p-10 shadow-level2 border border-outline-variant/10 print:border-none print:shadow-none print:p-0 space-y-8">
            <div className="text-center space-y-3 pb-8 border-b border-outline-variant/15">
              <div className="flex justify-center items-center gap-4">
                <img
                  src="/logo.png"
                  alt="TSL Logo"
                  className="h-14 sm:h-16 w-auto object-contain"
                />
              </div>
              <div>
                <h2 className="text-lg sm:text-2xl font-headline font-black text-on-surface tracking-wider uppercase">
                  TARBIYAH SUNNAH LEARNING
                </h2>
                <p className="text-xs sm:text-sm font-body text-on-surface-variant font-medium">
                  Dokumen Transkrip Akademik Resmi • Rekapitulasi Riwayat Sistem Sebelumnya
                </p>
              </div>
              <div className="inline-block px-4 py-1.5 rounded-full bg-secondary/15 text-secondary font-headline font-bold text-xs sm:text-sm uppercase tracking-widest">
                <Bilingual {...copy.transcripts.legacyOfficialDocTitle} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-xs font-body p-5 rounded-2xl bg-surface-container-low/40 border border-outline-variant/10">
              <div>
                <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                  <Bilingual {...copy.transcripts.studentName} />
                </span>
                <span className="text-sm font-headline font-bold text-on-surface block mt-0.5">
                  {legacyData?.user.name}
                </span>
              </div>

              <div>
                <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                  <Bilingual {...copy.transcripts.studentId} />
                </span>
                <span className="text-sm font-mono font-bold text-primary block mt-0.5">
                  {participantNip || legacyData?.user.userCode || '-'}
                </span>
              </div>

              <div>
                <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                  <Bilingual {...copy.transcripts.programParentName} />
                </span>
                <span className="text-sm font-headline font-bold text-on-surface block mt-0.5 line-clamp-1">
                  {selectedLegacyProgram !== 'ALL'
                    ? selectedLegacyProgram
                    : legacyData?.programs?.[0]?.programName || 'Belajar Islam Dasar (BID)'}
                </span>
              </div>

              <div>
                <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                  <Bilingual {...copy.transcripts.cumulativeGpa} />
                </span>
                <span className="text-sm font-mono font-bold text-primary block mt-0.5">
                  {legacyData?.summary.cumulativeGpa != null
                    ? legacyData.summary.cumulativeGpa.toFixed(2)
                    : '-'}{' '}
                  / 4.00 ({legacyData?.summary.overallPredicate.letter})
                </span>
              </div>
            </div>

            <div className="space-y-8">
              {groupedLegacySemesters.map(group => (
                <div
                  key={group.semester}
                  className="space-y-3 rounded-2xl overflow-hidden border border-outline-variant/15 bg-surface-container-lowest shadow-xs"
                >
                  <div className="bg-surface-container-low px-6 py-3.5 flex flex-wrap justify-between items-center gap-2 border-b border-outline-variant/15">
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 rounded-xl bg-primary text-on-primary text-xs font-label font-bold uppercase tracking-wider">
                        Semester {group.semester || '1'}
                      </span>
                      <span className="text-xs font-headline font-bold text-on-surface">
                        {group.subjects.length} Mata Kuliah Terdata
                      </span>
                    </div>

                    <div className="flex items-center gap-6 text-xs font-body">
                      {group.semesterGpa != null && (
                        <div>
                          <span className="text-on-surface-variant opacity-70">IPS: </span>
                          <span className="font-mono font-bold text-primary">
                            {group.semesterGpa.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="text-on-surface-variant opacity-70">Beban: </span>
                        <span className="font-mono font-bold text-on-surface">
                          {group.totalCredits} SKS
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-surface-container-low/30 text-on-surface-variant text-[10px] sm:text-xs font-headline font-bold uppercase tracking-wider border-b border-outline-variant/10">
                          <th className="p-3 sm:p-4 text-center w-12">No</th>
                          <th className="p-3 sm:p-4">
                            <Bilingual {...copy.transcripts.courseName} />
                          </th>
                          <th className="p-3 sm:p-4 text-center">
                            <Bilingual {...copy.transcripts.creditHours} />
                          </th>
                          <th className="p-3 sm:p-4 text-center">
                            <Bilingual {...copy.transcripts.numericalGrade} />
                          </th>
                          <th className="p-3 sm:p-4 text-center">
                            <Bilingual {...copy.transcripts.letterGradeCol} />
                          </th>
                          <th className="p-3 sm:p-4 text-center">
                            <Bilingual {...copy.transcripts.predicateCol} />
                          </th>
                          <th className="p-3 sm:p-4 text-center">
                            <Bilingual {...copy.transcripts.statusCol} />
                          </th>
                          <th className="p-3 sm:p-4 text-center print:hidden">Aksi</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-outline-variant/10 text-xs font-body">
                        {group.subjects.map((subj, idx) => (
                          <tr
                            key={subj.id}
                            className="hover:bg-surface-container-low/40 transition-colors"
                          >
                            <td className="p-3 sm:p-4 text-center font-mono text-on-surface-variant">
                              {idx + 1}
                            </td>
                            <td className="p-3 sm:p-4">
                              <span className="font-headline font-bold text-on-surface block text-sm">
                                {subj.subjectName || subj.program.name}
                              </span>
                              <div className="flex items-center gap-2 mt-0.5">
                                {(subj.subjectCode || subj.program.code) && (
                                  <span className="text-[10px] font-mono text-primary font-bold">
                                    {subj.subjectCode || subj.program.code}
                                  </span>
                                )}
                                {subj.programName && (
                                  <span className="text-[10px] text-on-surface-variant opacity-70">
                                    • {subj.programName}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="p-3 sm:p-4 text-center font-mono font-medium">
                              {subj.credit ?? 1}
                            </td>
                            <td className="p-3 sm:p-4 text-center font-mono font-bold text-on-surface">
                              {subj.totalGrades != null
                                ? subj.totalGrades.toFixed(1)
                                : subj.totalPercentage != null
                                  ? subj.totalPercentage.toFixed(1)
                                  : '-'}
                            </td>
                            <td className="p-3 sm:p-4 text-center font-headline font-bold text-primary text-sm">
                              {subj.predicateDetails.letter}
                            </td>
                            <td className="p-3 sm:p-4 text-center">
                              <span className="text-xs font-arabic font-bold block text-primary">
                                {subj.predicateDetails.arabic}
                              </span>
                              <span className="text-[10px] text-on-surface-variant font-medium">
                                {subj.predicateDetails.indonesian}
                              </span>
                            </td>
                            <td className="p-3 sm:p-4 text-center">
                              <span
                                className={`px-2.5 py-1 rounded-full text-[10px] font-headline font-bold tracking-wider uppercase ${
                                  subj.isCourseFinished
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-surface-container-high text-on-surface-variant'
                                }`}
                              >
                                {subj.isCourseFinished ? 'LULUS' : 'BERJALAN'}
                              </span>
                            </td>
                            <td className="p-3 sm:p-4 text-center print:hidden">
                              <button
                                type="button"
                                onClick={() => setDetailModalSubject(subj)}
                                className="px-3 py-1 rounded-lg bg-surface-container-high hover:bg-surface-container text-on-surface-variant text-[11px] font-label font-bold uppercase tracking-wider transition-colors"
                              >
                                Detail
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {groupedLegacySemesters.length === 0 && (
                <div className="py-16 text-center text-on-surface-variant opacity-60 font-body">
                  <p className="text-sm font-headline font-bold">
                    <Bilingual {...copy.transcripts.legacyEmptyTitle} />
                  </p>
                  <p className="text-xs mt-1">
                    <Bilingual {...copy.transcripts.legacyEmptyDesc} />
                  </p>
                </div>
              )}
            </div>

            <div className="pt-8 border-t border-outline-variant/15 flex flex-col sm:flex-row justify-between items-center gap-6">
              <div className="space-y-2 text-center sm:text-left max-w-sm">
                <p className="text-[11px] text-on-surface-variant leading-relaxed m-0">
                  Transkrip riwayat ini diimpor secara sah dari database perkuliahan Tarbiyah Sunnah
                  Learning terdahulu.
                </p>
                <div className="p-2 rounded-xl bg-surface-container-low border border-outline-variant/15 font-mono text-[10px] text-on-surface-variant">
                  STATUS: ARSIP HISTORI TERVERIFIKASI
                </div>
              </div>

              <div className="text-center space-y-2 sm:self-end">
                <span className="text-[10px] text-on-surface-variant font-medium block">
                  Bandung,{' '}
                  {new Date().toLocaleDateString('id-ID', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
                <div className="h-14 flex items-center justify-center">
                  <div className="w-28 h-10 border border-dashed border-primary/30 rounded-lg flex items-center justify-center text-[10px] font-bold text-primary/40 uppercase tracking-widest">
                    Tanda Tangan
                  </div>
                </div>
                <span className="text-xs font-headline font-bold text-on-surface block">
                  <Bilingual {...copy.transcripts.directorSignature} />
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {detailModalSubject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-fadeIn">
          <div className="bg-surface-container-lowest rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-level3 border border-on-surface/5 space-y-6 animate-scaleUp">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <span className="text-[10px] font-label font-bold uppercase tracking-widest text-primary">
                  Detail Mata Kuliah Masa Lalu
                </span>
                <h3 className="text-xl font-headline font-bold text-on-surface m-0">
                  {detailModalSubject.program.name}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setDetailModalSubject(null)}
                className="p-2 rounded-xl text-on-surface-variant hover:bg-surface-container transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs font-body">
              <div className="p-3.5 rounded-2xl bg-surface-container-low space-y-1">
                <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                  Kode Mata Kuliah
                </span>
                <span className="font-mono font-bold text-on-surface text-sm">
                  {detailModalSubject.program.code || '-'}
                </span>
              </div>

              <div className="p-3.5 rounded-2xl bg-surface-container-low space-y-1">
                <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                  Semester
                </span>
                <span className="font-mono font-bold text-on-surface text-sm">
                  Semester {detailModalSubject.semester || '-'}
                </span>
              </div>

              <div className="p-3.5 rounded-2xl bg-surface-container-low space-y-1">
                <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                  Beban SKS
                </span>
                <span className="font-mono font-bold text-on-surface text-sm">
                  {detailModalSubject.credit ?? 1} SKS
                </span>
              </div>

              <div className="p-3.5 rounded-2xl bg-surface-container-low space-y-1">
                <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                  Nilai Angka / Persentase
                </span>
                <span className="font-mono font-bold text-primary text-sm">
                  {detailModalSubject.totalGrades != null
                    ? detailModalSubject.totalGrades.toFixed(1)
                    : '-'}
                </span>
              </div>

              <div className="p-3.5 rounded-2xl bg-surface-container-low space-y-1">
                <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                  Nilai Huruf
                </span>
                <span className="font-headline font-black text-primary text-base">
                  {detailModalSubject.predicateDetails.letter}
                </span>
              </div>

              <div className="p-3.5 rounded-2xl bg-surface-container-low space-y-1">
                <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                  Predikat Yudisium
                </span>
                <span className="font-arabic font-bold text-secondary text-sm block">
                  {detailModalSubject.predicateDetails.arabic}
                </span>
                <span className="text-[10px] text-on-surface-variant font-medium">
                  {detailModalSubject.predicateDetails.indonesian}
                </span>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => setDetailModalSubject(null)}
                className="px-6 py-2.5 rounded-xl bg-primary text-on-primary text-xs font-label font-bold uppercase tracking-wider shadow-xs hover:bg-primary/90 transition-all"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Action Navigation Bar */}
      <div className="print:hidden flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-outline-variant/10">
        <Link
          to="/results"
          className="inline-flex items-center gap-2 text-xs font-bold text-on-surface-variant hover:text-primary transition-colors"
        >
          <ArrowLeft size={16} />
          <span>
            <Bilingual {...copy.transcripts.backToResults} />
          </span>
        </Link>

        {selectedEnrollment?.status === 'COMPLETED' && (
          <Link
            to="/certificates"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-secondary text-on-secondary text-xs font-label font-bold uppercase tracking-wider shadow-sm hover:opacity-90 transition-opacity"
          >
            <Award size={16} />
            <span>
              <Bilingual {...copy.transcripts.viewCertificate} />
            </span>
          </Link>
        )}
      </div>
    </div>
  );
}
